import { useReducer, useRef, useState } from 'react';
import { feedbackReducer, INITIAL, buildRequest, Rating } from './feedbackMachine';
import { assistantStorage } from './assistantStorage';
import { createEpisode, updateEpisode } from '../../services/feedbackBot.service';

type Target = { sessionId: string; messageId: string };
/** Contexto de un destino: su episodio se crea una sola vez (promesa en caché). */
type Ctx = { target: Target | null; kind: 'auto' | 'manual'; episode: Promise<string> | null };

export function useAssistant() {
  const [state, dispatch] = useReducer(feedbackReducer, INITIAL);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Target | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [optedOut, setOptedOut] = useState<boolean>(() => assistantStorage.getOptOut());
  // El servidor respondió 503 (bot desactivado): se deja de preguntar durante esta sesión.
  const [disabled, setDisabled] = useState(false);
  const disabledRef = useRef(false);
  const ctx = useRef<Ctx>({ target: null, kind: 'auto', episode: null });
  // Todos los PATCH pasan por una sola cadena: llegan al servidor en el orden en que se emitieron.
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const ensureEpisode = (c: Ctx): Promise<string> | null => {
    if (!c.target) return null;
    if (!c.episode) {
      c.episode = createEpisode(c.target.messageId, c.kind).then(ep => ep.id);
      c.episode.catch(() => { c.episode = null; }); // permite reintentar tras un fallo
    }
    return c.episode;
  };

  /** Encola un PATCH (creando el episodio si falta). Resuelve true si se guardó. */
  const patch = (body: Record<string, unknown>): Promise<boolean> => {
    setError(null);
    const c = ctx.current;
    const run = queue.current.then(async () => {
      const id = await ensureEpisode(c);
      if (id) await updateEpisode(id, body);
    });
    queue.current = run.catch(() => undefined);
    return run.then(() => true, (e: unknown) => {
      if ((e as { status?: number } | null)?.status === 503) {
        disabledRef.current = true;
        setDisabled(true); setOpen(false); setError(null);
      } else setError('No se pudo guardar. Intenta de nuevo.');
      return false;
    });
  };

  const newCtx = (t: Target | null, kind: 'auto' | 'manual') => { ctx.current = { target: t, kind, episode: null }; };

  /** Disparo automático: la primera vez por conversación el panel se abre solo. */
  const startFor = (t: Target, kind: 'auto' | 'manual') => {
    if (disabledRef.current) return;
    newCtx(t, kind); setTarget(t); setError(null);
    dispatch({ type: 'RESET' });
    if (kind === 'auto') setOpen(true);
  };

  /** Cambio de conversación: olvida el destino y el episodio; `open` no se toca. */
  const reset = () => {
    newCtx(null, 'auto'); setTarget(null); setError(null);
    dispatch({ type: 'RESET' });
  };

  const rate = (rating: Rating) => {
    dispatch({ type: 'RATE', rating });
    patch(rating === 'util' ? { rating, outcome: 'registrado' } : { rating });
  };
  const answer = (optionIds: string[], text?: string): boolean => {
    const next = feedbackReducer(state, { type: 'ANSWER', optionIds, text });
    if (next === state) return false;
    dispatch({ type: 'ANSWER', optionIds, text });
    // Lista completa vigente: el servidor la reemplaza, así no hay duplicados ni desorden.
    patch({ answers: next.answers.map(a => ({ pregunta: a.pregunta, respuesta: a.respuesta })) });
    return true;
  };
  const confirm = () => {
    const request = buildRequest(state);
    dispatch({ type: 'CONFIRM' });
    patch({ revision_request: request, outcome: 'registrado' });
  };
  const correct = () => {
    dispatch({ type: 'CORRECT' });
    patch({ answers: [] });
  };
  const snooze = () => {
    assistantStorage.snooze(Date.now());
    dispatch({ type: 'DISMISS' });
    if (ctx.current.target) patch({ outcome: 'pospuesto' });
  };
  const optOut = () => {
    assistantStorage.setOptOut(true);
    setOptedOut(true);
    dispatch({ type: 'DISMISS' });
    if (ctx.current.target) patch({ outcome: 'excluido' });
  };
  const optIn = () => { assistantStorage.setOptOut(false); setOptedOut(false); };
  const comment = (text: string): Promise<boolean> => {
    if (!text.trim()) return Promise.resolve(false);
    return patch({ comment: text.trim() });
  };

  const hasTarget = !!target;
  const dot = !open && hasTarget && !disabled && state.phase === 'asking';
  return {
    state, open, setOpen, hasTarget, dot, error, target, optedOut, disabled,
    startFor, reset, rate, answer, confirm, correct, snooze, optOut, optIn, comment,
  };
}
